import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { TranslateService } from '@ngx-translate/core';
import { INFOS } from '../common/utils/notification-constants';
import { ResourceItemModel } from '../models/resource';
import { IMAGES_DEFAULT_WIDTH } from '../sessions/session/iframe/additional-apps.utils';
import { SpaceRepository } from '../state/space.repository';
import { NotificationToasterService } from './notification-toaster.service';
import { BoundingRect } from './sessions-vpt.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { SpaceBoardsService } from './space-boards.service';
import { UploadFile } from './document-upload.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenshotService {
  constructor(
    private sharedDataService: SessionSharedDataService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private spaceRepo: SpaceRepository,
    private spaceBoardServices: SpaceBoardsService,
  ) {}

  public async takeScreenshot(
    htmlElement: HTMLVideoElement | HTMLDivElement,
    elementWidth: number,
    elementHeight: number,
    screenshotBoundingRect?: BoundingRect,
  ): Promise<void>;
  public async takeScreenshot(
    arg1: HTMLVideoElement | HTMLDivElement,
    arg2?: number,
    arg3?: number,
    arg4?: { top: number; left: number; height: number; width: number },
  ): Promise<void> {
    this.showLoadingIndicator();
    let canvas;
    if (arg1 instanceof HTMLVideoElement) {
      canvas = await this.processVideo(arg1, arg2!, arg3!);
    } else if (arg1 instanceof HTMLDivElement) {
      canvas = await this.processDiv(arg1);
    }
    this.canvasToBoardImage(canvas, arg4);
  }

  private async processVideo(
    htmlElement: HTMLVideoElement,
    elementWidth: number,
    elementHeight: number,
  ): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    canvas.width = elementWidth;
    canvas.height = elementHeight;
    canvas?.getContext('2d')?.drawImage(htmlElement, 0, 0);
    return canvas;
  }

  private async processDiv(htmlElement: HTMLDivElement): Promise<HTMLCanvasElement> {
    return html2canvas(htmlElement, {
      backgroundColor: 'rgba(0,0,0,0)',
      allowTaint: false,
      useCORS: true,
      onclone: (document, element) => {
        // for elements cloned in an invisible container
        element.style.setProperty('opacity', '1');
      },
    });
  }

  private canvasToBoardImage(
    canvas: HTMLCanvasElement,
    screenshotBoundingRect?: BoundingRect,
  ): void {
    // creating a file from the stream and uploading it to the space.
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'screenshot.png', { type: 'image/png' });
        this.hideLoadingIndicator();
        if (
          this.spaceRepo.activeSpace?._id &&
          this.spaceBoardServices.activeSpaceSelectedBoard?.uid
        ) {
          this.sharedDataService.uploadItem.next([
            new UploadFile(
              file,
              file.name,
              screenshotBoundingRect?.width ?? IMAGES_DEFAULT_WIDTH,
              ResourceItemModel.IMAGE,
              this.spaceRepo.activeSpace._id,
              this.spaceBoardServices.activeSpaceSelectedBoard?.uid,
              {
                ...(screenshotBoundingRect ?? {}),
                renderInNearestFreeSpace: true,
              },
            ),
          ]);
        }
      } else {
        this.hideLoadingIndicator();
      }
    }, 'image/png');
  }

  showLoadingIndicator(): void {
    this.notificationToasterService.showLoadingNotification(
      `${this.translateService.instant('Taking screenshot')}...`,
      INFOS.TAKING_SCREENSHOT,
      true,
    );
  }

  hideLoadingIndicator(): void {
    this.notificationToasterService.dismissLoadingNotification(INFOS.TAKING_SCREENSHOT);
  }
}
